import { api_configuration } from "../../../common";
import gui_definition_types = api_configuration.libraries.gui_definition_lib.types;
import { client_state } from "../../state/ClientState";
import csr = api_configuration.types.client_state.alan_api;
import DestructorTree from "../../DestructorTree";
import renderView = require("../renderView");
import { createIdPathFromNode, createIdPathBuilderFromNode } from "../../navigation/createIdPathFromNode";
import { IdPathBuilder } from "../../navigation/IdPathBuilder";
import uuid = require("../../state/uuid");
import createClientIdPathFromNode from "../../navigation/createClientIdPathFromNode";
import hash = require("./hash");
import { isDefined, isNonNull } from "../../validation";
import { subscribeOnSave, unSubscribeOnSave } from "../instructions/save";
import { ControlsAreSynchronized } from "../ControlLibrary";
import { linkIdPath } from "../../state/ClientIdPath";

function pushHistoryState(deeplink_hash: string, title: string) {
	if (window.location.hash !== "#" + deeplink_hash) {
		window.history.pushState(undefined, title, window.location.origin + window.location.pathname + "#" + deeplink_hash);
	}
}

function generateNodeEntryKeyPartOfViewName(
	node: csr.Cnode,
	on_update: (new_value: string, on_removed: () => void) => void
): void {
	node.input.entity().switch({
		root: function () {
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			on_update("", () => {});
		},
		entry: function ($) {
			const mutable_text = $.properties.key.ref;
			mutable_text.properties.is_set.subscribe({
				on_update: function (_, subscription) {
					const new_value = $.properties.key.ref.properties.is_set.switch({
						no: "",
						yes: ($) => $.properties.text,
					});
					on_update(new_value, () => {
						subscription.unsubscribe();
					});
				},
			});
		},
	});
}

function isEntryView(node: csr.Cnode): boolean {
	const entity = node.input.entity();
	switch (entity.variant.name) {
		case "root":
			return false;
		case "entry":
			return true;
		default:
			return unexpectedState(entity.variant);
	}
}

function translate(phrase: string): string {
	return client_state.properties.selected_language.ref.key.ref.properties.translations.switchOnEntryExists(
		phrase,
		(translation) => translation.properties.translation,
		() => phrase
	);
}

function generateNodePathPartOfViewName(node: csr.Cnode): string {
	return node.input.position().switch({
		entity: ($) =>
			$.switch({
				root: () => "",
				entry: function ($) {
					const collection_name = translate($.parent.parent.parent.key.ref.key);
					return generateNodePathPartOfViewName($.parent.parent.parent.parent) + " › " + collection_name;
				},
			}),
		group: function ($) {
			const group_name = translate($.parent.parent.key.ref.key);
			return generateNodePathPartOfViewName($.parent.parent.parent) + " › " + group_name;
		},
		state: function ($) {
			const state_name = translate($.key.ref.key);
			const state_group_name = translate($.parent.parent.parent.key.ref.key);
			return (
				generateNodePathPartOfViewName($.parent.parent.parent.parent) + " › " + state_group_name + " › " + state_name
			);
		},
		root: ($) =>
			$.switch({
				root: () => "",
				operation: ($) => {
					const operation_name = translate($.parent.parent.key.ref.key);
					return generateNodePathPartOfViewName($.parent.parent.parent) + " › " + operation_name;
				},
			}),
	});
}

type HasOpenView = "no" | csr.Copen_views;

const windows = new Map<string, Window>();

class View {
	view_containing_element: () => HTMLElement;
	destructor: DestructorTree;
	definition: csr.Copen_views;
	active_element: Element | null;
	private in_front = false;
	private created_element: HTMLElement;

	constructor(window_containing_element: () => HTMLElement, destructor: DestructorTree, definition: csr.Copen_views) {
		this.view_containing_element = () => {
			if (this.created_element === undefined) {
				this.created_element = isNonNull(window_containing_element().ownerDocument).createElementNS(
					"http://www.w3.org/1999/xhtml",
					"div"
				);
				if (this.in_front) {
					this.created_element?.classList.add("in-front");
				}
				this.created_element.classList.add("engine-window");
				window_containing_element().appendChild(this.created_element);
				this.destructor.onDestroy(() => {
					this.created_element.remove();
				});
			}
			return this.created_element;
		};
		this.destructor = destructor;
		this.definition = definition;

		renderView.render(
			this.view_containing_element,
			this.definition.properties.view_definition.ref,
			this.definition.properties.view_context.output.result_node(),
			this.destructor,
			this.view_containing_element,
			this.definition
		);
	}

	moveToFront() {
		this.created_element?.classList.add("in-front");
		this.in_front = true;
		setTimeout(() => {
			if (this.active_element instanceof HTMLElement) {
				this.active_element.focus();
			}
		}, 10);
	}

	moveToBack() {
		this.created_element?.classList.remove("in-front");
		this.in_front = false;
	}

	close() {
		this.destructor.destroy();
	}
}

function bindEntryKey(view: csr.Copen_views, node: csr.Cnode, view_destructor: DestructorTree) {
	const gui_view = view.properties.view_definition.ref;
	const mutable_added_view = view;

	if (isEntryView(view.properties.view_context.output.result_node()) === true) {
		let unsubscribe_from_entry_key: (() => void) | undefined;

		mutable_added_view.properties.is_entry_view.set("yes", {
			"entry key": "",
			"entry path": "",
		});
		const mutable_is_entry_view = mutable_added_view.properties.is_entry_view.cast("yes");

		generateNodeEntryKeyPartOfViewName(
			view.properties.view_context.output.result_node(),
			function (new_entry_key, unsubscribe) {
				if (view.destroyed) {
					unsubscribe();
				} else {
					unsubscribe_from_entry_key = unsubscribe;
					mutable_is_entry_view.set_entry_key(new_entry_key);
				}
			}
		);

		view_destructor.onDestroy(() => {
			unsubscribe_from_entry_key?.();
		});
	} else {
		mutable_added_view.properties.is_entry_view.set("no", {
			title: "",
		});
	}

	const make_title = gui_view.properties.translate_title.switch({
		no: () => () => generateNodePathPartOfViewName(node),
		yes: ($) => {
			const gui_view_title = $.properties.title.entry;
			return () => {
				if (gui_view_title.trim() === "") {
					return generateNodePathPartOfViewName(node);
				} else {
					return translate(gui_view_title);
				}
			};
		},
	});

	const language_observer = client_state.subscribe_selected_language({
		on_update: function () {
			const title = make_title();
			view.properties.is_entry_view.switch({
				no: ($) => {
					$.set_title(title);
				},
				yes: ($) => {
					$.set_entry_path(title);
				},
			});
		},
	});
	view_destructor.onDestroy(() => {
		language_observer.unsubscribe();
	});
}

const OPEN_VIEWS_THRESHOLD = 10;

export class Window {
	window_containing_element: () => HTMLElement;
	open_views: { [key: string]: View } = {};
	open_views_history: View[] = [];
	definition: csr.Cwindows;
	destructor: DestructorTree;
	deeplink_target = false;

	constructor(window_containing_element: () => HTMLElement, destructor: DestructorTree, definition: csr.Cwindows) {
		this.window_containing_element = window_containing_element;
		this.definition = definition;
		this.destructor = destructor;
		this.destructor.onDestroy(() => {
			if (this.definition.destroyed) {
				return;
			}
			for (const [, open_view] of this.definition.properties.open_views) {
				this.close_view(open_view);
			}
		});
		this.deeplink_target = definition.key.ref.parent.properties.allow_deeplink.switch({
			yes: function ($) {
				const is_target = $.properties.target_window.ref === definition.key.ref;
				if (is_target) {
					const on_saved = subscribeOnSave((saved_node) => {
						definition.properties.has_active_view.switch({
							no: undefined,
							yes: function ($) {
								const active_view = $.properties.view.ref;
								const node_of_view = active_view.properties.view_context.output.result_node();
								if (saved_node === node_of_view) {
									pushHistoryState(
										hash.deepLinkTo(
											active_view.properties.view_definition_context.entry,
											active_view.properties.view_definition.entry,
											createIdPathBuilderFromNode(node_of_view)
										),
										active_view.properties.view_definition.entry
									);
								}
							},
						});
					});
					destructor.onDestroy(() => {
						unSubscribeOnSave(on_saved);
						hash.clearDeepLink();
					});
				}
				return is_target;
			},
			no: false,
		});
	}

	addView(node: csr.Cnode, gui_view: gui_definition_types.views.alan_api.Cviews__, subscribe = true): csr.Copen_views {
		if (node.output.node_type() === gui_view.properties.node_type.output.result_gui_node().input.application_node()) {
			// Check if the view is a view of the current node
			const open_view = this.is_view_open(node, gui_view);
			let view: View;
			if (open_view !== "no") {
				view = this.open_views[open_view.key];
			} else {
				const view_id = uuid.generate();
				const added_view = this.definition.properties.open_views.create(view_id, {
					"is active view": ["no", {}],
					"is entry view": [
						"no",
						{
							title: "",
						},
					],
					"action active": ["no", {}],
					"view context": createClientIdPathFromNode(node),
					"view definition context": isDefined(gui_view.parent.key),
					"view definition": gui_view.key,
					subscribe: subscribe ? "yes" : "no",
				});
				linkIdPath(added_view.properties.view_context);
				const view_destructor = this.destructor.addChild();
				bindEntryKey(added_view, node, view_destructor);
				this.open_views[view_id] = new View(this.window_containing_element, view_destructor, added_view);
				view = this.open_views[view_id];
			}
			return view.definition;
		} else {
			throw new Error("invalid view");
		}
	}

	open_view(
		node: csr.Cnode,
		gui_view: gui_definition_types.views.alan_api.Cviews__,
		on_activated?: (view: csr.Copen_views) => void,
		in_front = true
	): csr.Copen_views {
		const view = this.addView(node, gui_view);
		setTimeout(() => {
			this.activate_view(view, in_front);
			on_activated?.(view);
			this.closeInactiveViews();
		}, 50); // delay the rendering to give the backend state some time to initialize.
		return view;
	}

	is_view_open(
		context: IdPathBuilder | csr.Cnode,
		gui_view: gui_definition_types.views.alan_api.Cviews__
	): HasOpenView {
		let view_open: HasOpenView = "no";
		if (context instanceof csr.Cnode) {
			for (const open_view of this.definition.properties.open_views.values())
				if (open_view.properties.view_definition.ref.is(gui_view))
					if (context.is(open_view.properties.view_context.output.result_node())) view_open = open_view;
		} else {
			for (const open_view of this.definition.properties.open_views.values())
				if (open_view.properties.view_definition.ref.is(gui_view))
					if (
						JSON.stringify(createIdPathFromNode(open_view.properties.view_context.output.result_node())) ===
						JSON.stringify(context.build())
					)
						view_open = open_view;
		}
		return view_open;
	}

	activate_view(view: csr.Copen_views, in_front = true): void {
		if (view.destroyed) {
			return;
		}
		this.definition.properties.has_active_view.switch({
			no: () => {
				in_front = true;
			},
			yes: async ($) => {
				if (in_front) {
					const active_view = $.properties.view.ref;
					this.definition.properties.has_active_view.set("no", {});
					active_view.properties.is_active_view.set("no", {});
					const open_view = this.open_views[active_view.key];
					open_view.moveToBack();
					const active_element = document.activeElement;
					await ControlsAreSynchronized();
					if (open_view.view_containing_element().contains(active_element)) {
						open_view.active_element = active_element;
					}
				}
			},
		});
		const open_view = this.open_views[view.key];
		this.open_views_history = this.open_views_history.filter(
			(a_view_from_history) => a_view_from_history !== open_view
		);
		if (in_front) {
			view.properties.is_active_view.set("yes", {});
			this.definition.properties.has_active_view.set("yes", {
				view: view.key,
			});
			open_view.moveToFront();
			if (this.deeplink_target) {
				const node = open_view.definition.properties.view_context.output.result_node();
				node.properties.location.switch({
					local: undefined,
					remote: function () {
						pushHistoryState(
							hash.deepLinkTo(
								isDefined(view.properties.view_definition.ref.parent.key),
								view.properties.view_definition.ref.key,
								createIdPathBuilderFromNode(node)
							),
							view.properties.view_definition.ref.key
						);
					},
				});
			}
			//insert at last place
			this.open_views_history.splice(this.open_views_history.length, 0, open_view);
		} else {
			//insert at second to last place
			this.open_views_history.splice(this.open_views_history.length - 1, 0, open_view);
		}
	}

	close_view(view: csr.Copen_views): void {
		const open_view = this.open_views[view.key];
		if (!open_view) {
			return;
		}
		const mutable_window = this.definition;

		this.open_views_history = this.open_views_history.filter(function (a_view_from_history) {
			return a_view_from_history !== open_view;
		});
		this.definition.properties.has_active_view.switch({
			no: undefined,
			yes: ($) => {
				if ($.properties.view.ref.key === view.key) {
					if (this.open_views_history.length > 0) {
						this.activate_view(this.open_views_history[this.open_views_history.length - 1].definition);
					} else {
						if (this.deeplink_target) {
							pushHistoryState(hash.clearDeepLink(), "");
						}
						mutable_window.properties.has_active_view.set("no", {});
					}
				}
			},
		});
		open_view.close();
		mutable_window.properties.open_views.delete(view.key);
		delete this.open_views[view.key];
	}

	private closeInactiveViews() {
		const number_of_open_views = this.open_views_history.length;
		if (number_of_open_views <= OPEN_VIEWS_THRESHOLD) {
			return;
		}
		let closed_views = 0;
		const views_to_close = number_of_open_views - OPEN_VIEWS_THRESHOLD;
		const history_copy = this.open_views_history.slice(); //use a copy because the original is going to change in the loop
		for (let i = 0; i < number_of_open_views && closed_views < views_to_close; i += 1) {
			const view_definition = history_copy[i].definition;
			if (!view_definition.destroyed) {
				view_definition.properties.is_active_view.switch({
					no: () => {
						const context_node = view_definition.properties.view_context.output.result_node();
						if (context_node === undefined) {
							closed_views += 1;
							this.close_view(view_definition);
						} else {
							context_node.properties.modified.switch({
								no: () => {
									closed_views += 1;
									this.close_view(view_definition);
								},
								yes: undefined,
							});
						}
					},
					yes: undefined,
				});
			}
		}
	}
}

export function getWindowInstance(name: string): Window {
	const window = windows.get(name);
	if (window !== undefined) {
		return window;
	} else {
		throw new Error(`Window '${name}' is not initialized`);
	}
}

export function initialize(
	window_name: string,
	window_containing_element: () => HTMLElement,
	destructor: DestructorTree,
	definition: csr.Cwindows
): void {
	windows.set(window_name, new Window(window_containing_element, destructor, definition));
	destructor.onDestroy(() => {
		definition.parent.properties.windows.delete(window_name);
		windows.delete(window_name);
	});
}
